/**
 * Overlays usage information, collected via the UsageLogger, on JavaScript UIs
 */
define(["dojo/_base/declare", "dojo/_base/lang", "dojo/dom", "dojo/dom-construct", "dojo/dom-geometry", "dojo/dom-style", "mw-log/Log", "mw-messageservice/MessageService", "mw-remote/Remote"], function (declare, lang, dom, domConstruct, domGeometry, domStyle, Log, MessageService, Remote) {

    var enabled;
    var overlay;
    var popupOverlay;
    var _product;
    var _scope;
    var release;
    var showShading;
    var showNumbers;
    var useLogScale;
    var maxMetric;
    var elementMap;
    var isIntegerMetric;
    var pendingLabels;

    var CHANNEL_ROOT = "/UIEventOverlayData";

    return {

        /**
         * Enables overlay display.  This will not directly cause the overlay to appear.  Instead
         * it will subscribe to a channel to which usage metric data may be published.  The overlay
         * will appear when such data is received.
         *
         * @param product {string} the product name.  This identifies the metric that should be displayed
         * @param scope {string} the scope name which typically corresponds to a particular toolbox UI or App.  This
         *                       is also used to identify the metric that should be displayed.
         */
        enable: function enable(product, scope) {
            if (enabled) {
                return;
            }
            Log.assert(product, "Must specify product");
            Log.assert(scope, "Must specify scope");
            _product = product;
            _scope = scope;
            enabled = true;
            var that = this;
            var channel = CHANNEL_ROOT + "/" + product.replace(/ /g, '_') + ":" + scope.replace(/ /g, '_');
            if (!MessageService.isRunning()) {
                MessageService.start();
            }
            MessageService.subscribe(channel, function (message) {
                var show = message.data.showShading || message.data.showNumbers;
                if (show) {
                    release = message.data.release;
                    showShading = message.data.showShading;
                    showNumbers = message.data.showNumbers;
                    isIntegerMetric = message.data.isIntegerMetric;
                    useLogScale = message.data.useLogScale;
                    maxMetric = message.data.maxMetric;
                    // Construct element map
                    var elementId = message.data.elementId;
                    var metric = message.data.metric;
                    var isLabelKnown = message.data.isLabelKnown;
                    elementMap = {};
                    for (var i = 0; i < elementId.length; i++) {
                        if (elementMap[elementId[i]]) {
                            // Can be multiple entries, clicks and key presses for an element
                            elementMap[elementId[i]].metric += metric[i];
                        } else {
                            elementMap[elementId[i]] = {
                                metric: metric[i],
                                isLabelKnown: isLabelKnown[i]
                            };
                        }
                    }
                } else {
                    showShading = false;
                    showNumbers = false;
                    elementMap = null;
                }
                if (show && !overlay) {
                    that._addOverlays();
                } else if (!show && overlay) {
                    that._removeOverlays();
                }
                that._updateOverlays();
            });
        },

        _addOverlays: function _addOverlays() {
            var that = this;
            // Create two overlays, one at z-index 99 which is below all dialogs and popups
            // and one at 1001 which is above dialogs and popups other than tooltips.
            overlay = domConstruct.create("canvas", {
                id: "usage_overlay",
                width: window.innerWidth,
                height: window.innerHeight
            }, document.body);
            domStyle.set(overlay, { position: "absolute", left: 0, top: 0, "z-index": 99, "pointer-events": "none" });
            popupOverlay = domConstruct.create("canvas", {
                id: "usage_popup_overlay",
                width: window.innerWidth,
                height: window.innerHeight
            }, document.body);
            domStyle.set(popupOverlay, { position: "absolute", left: 0, top: 0, "z-index": 1001, "pointer-events": "none" });

            window.addEventListener('resize', function () {
                overlay.width = window.innerWidth;
                overlay.height = window.innerHeight;
                popupOverlay.width = window.innerWidth;
                popupOverlay.height = window.innerHeight;
                that._updateOverlays();
            });
            var domObserver = new MutationObserver(function (mutationList) {
                var childListChange;
                for (var i = 0; i < mutationList.length && !childListChange; i++) {
                    childListChange = mutationList[i].type === "childList";
                }
                if (childListChange) {
                    that._addScrollListeners(document.body);
                }
                that._updateOverlays();
            });
            domObserver.observe(document.body, { childList: true, attributes: true, subtree: true });
            this._scrollListener = lang.hitch(this, this._updateOverlays);
        },

        _removeOverlays: function _removeOverlays() {
            if (overlay) {
                document.body.removeChild(overlay);
                overlay = null;
            }
            if (popupOverlay) {
                document.body.removeChild(popupOverlay);
                popupOverlay = null;
            }
        },

        _updateOverlays: function _updateOverlays() {
            var context, overlayBox;
            if (overlay) {
                context = overlay.getContext("2d");
                overlayBox = domGeometry.getMarginBox(overlay);
                context.clearRect(0, 0, overlayBox.w, overlayBox.h);
                this._drawUsageRecursive(context, document.body, 0, 0, 99);
            }
            if (popupOverlay) {
                context = popupOverlay.getContext("2d");
                overlayBox = domGeometry.getMarginBox(popupOverlay);
                context.clearRect(0, 0, overlayBox.w, overlayBox.h);
                this._drawUsageRecursive(context, document.body, 0, 100, 1000);
            }
        },

        _drawUsageRecursive: function _drawUsageRecursive(context, node, inheritedZ, zLower, zUpper) {
            if (domStyle.get(node, "visibility") !== "hidden" && domStyle.get(node, "display") !== "none") {
                var zIndex = domStyle.get(node, "z-index");
                var effectiveZ = zIndex === "auto" ? inheritedZ : zIndex;
                if (effectiveZ >= zLower && effectiveZ <= zUpper) {
                    this._drawUsage(context, node);
                }
                if (node.children && node.children.length > 0) {
                    context.save();
                    var nodeBox = domGeometry.position(node);
                    context.beginPath();
                    context.rect(nodeBox.x, nodeBox.y, nodeBox.w, nodeBox.h);
                    context.clip();
                    for (var i = 0; i < node.children.length; i++) {
                        this._drawUsageRecursive(context, node.children[i], effectiveZ, zLower, zUpper);
                    }
                    context.restore();
                }
            }
        },

        _drawUsage: function _drawUsage(context, node) {
            var that = this;
            var tag = node.getAttribute("data-tag");
            if (tag) {
                var element = elementMap[tag];
                if (element) {
                    var nodeBox = domGeometry.position(node);
                    if (showShading) {
                        context.fillStyle = this._getHeatColor(element.metric);
                        context.fillRect(nodeBox.x, nodeBox.y, nodeBox.w, nodeBox.h);
                    }
                    if (showNumbers) {
                        this._drawNumber(context, nodeBox, element.metric);
                    }
                    if (!element.isLabelKnown) {
                        var label = this._findText(node);
                        if (label) {
                            element.isLabelKnown = true;
                            if (pendingLabels) {
                                pendingLabels[tag] = label;
                            } else {
                                pendingLabels = {};
                                pendingLabels[tag] = label;
                                setTimeout(function () {
                                    that._sendLabels(pendingLabels);
                                    pendingLabels = null;
                                }, 0);
                            }
                        }
                    }
                }
            }
        },

        _findText: function _findText(node) {
            // Ideally there would be a unifrom way to find widget text but for now we have the following
            // Button text can be obtained from an attribute
            var text = node.getAttribute("data-text-content");
            if (text) {
                return text;
            }
            // List item and toolstrip tab text can be obtained from mwTextLine node content
            if (node.classList.contains("mwTextLine")) {
                text = node.textContent;
                if (text) {
                    return text;
                }
            }
            if (node.children && node.children.length > 0) {
                for (var i = 0; i < node.children.length; i++) {
                    text = this._findText(node.children[i]);
                    if (text) {
                        return text;
                    }
                }
            }
        },

        _getHeatColor: function _getHeatColor(metric) {
            var alpha = 0.0;
            if (metric > 0) {
                alpha = useLogScale ? Math.log(metric) / Math.log(maxMetric) : metric / maxMetric;
                alpha = 0.5 * alpha + 0.04;
            }
            return "rgba(212, 0 , 0, " + alpha + ")";
        },

        _addScrollListeners: function _addScrollListeners(node) {
            if (node.children && node.children.length > 0) {
                node.addEventListener("scroll", this._scrollListener);
                for (var i = 0; i < node.children.length; i++) {
                    this._addScrollListeners(node.children[i]);
                }
            }
        },

        _drawNumber: function _drawNumber(context, box, metric) {
            if (metric === 0) {
                return;
            }
            var metricString = this._formatMetric(metric);
            context.font = "normal 14px Arial";
            var size = context.measureText(metricString);
            var width = size.width + 6;
            var height = size.width + 6;
            var x = box.x + (box.w - width) / 2;
            var y = box.y + (box.h - height) / 2;

            context.fillStyle = "rgb(255, 255, 255)";
            context.fillRect(x, y, width, height);
            context.strokeStyle = "rgb(128, 128, 128)";
            context.strokeRect(x, y, width, height);
            context.strokeStyle = "rgb(0, 0, 0)";
            context.strokeText(metricString, x + 3, y + 12);
        },

        _formatMetric: function _formatMetric(metric) {
            if (isIntegerMetric) {
                var suffix = "";
                if (metric >= 1000000000) {
                    metric = (metric + 500000000) / 1000000000;
                    suffix = "G";
                } else if (metric >= 1000000) {
                    metric = (metric + 500000) / 1000000;
                    suffix = "M";
                } else if (metric >= 10000) {
                    metric = (metric + 500) / 1000;
                    suffix = "K";
                }
                return metric + suffix;
            } else {
                var decimals;
                if (metric >= 10) {
                    decimals = 0;
                } else if (metric > 1) {
                    decimals = 1;
                } else if (metric > 0.01) {
                    decimals = 2;
                } else {
                    decimals = 3;
                }
                return metric.toFixed(decimals);
            }
        },

        _sendLabels: function _sendLabels(labels) {
            // Call matlab function to save discovered labels
            Remote.sendMessage({
                FEval: {
                    function: "uieventdataviewer.internal.saveLabels",
                    arguments: [_product, _scope, release, Object.keys(labels), Object.values(labels)],
                    nargout: 0,
                    priority: 1,
                    dequeMode: "ppe"
                }
            });
        }
    };
});

